<script lang="ts">
  import { loginUser } from '../../../components/auth/LoginUser';
  import type { GetBundleOptionResult } from '../../../repositories/bundleoption';
  import MailIspChangeModal from './MailIspChangeModal.svelte';
  import MailIspUtil from '../../../util/MailIspUtil';
  import RepositoryFactory, { BUNDLE_OPTION } from '../../../repositories/RepositoryFactory';
  import Loading from '../../loader.svelte';

  export let props: GetBundleOptionResult;

  $: isLoading = false;
  let pageStatus: 'INPUT' | 'COMPLETE' | 'BACK_MAIL' = 'INPUT';

  const currentMailPassword = props.outboundKey2;
  let changedMailPassword: string = '';
  let errorMessage: string = '';

  const handleTransitionComplete = (): void => {
    errorMessage = '';

    if (!changedMailPassword) {
      errorMessage = 'パスワードを入力してください';
      return;
    }

    if (!MailIspUtil.isValidPasswordLength(changedMailPassword)) {
      errorMessage = 'パスワードは3文字以上16文字以内で入力してください';
      return;
    }

    if (!MailIspUtil.isAvailablePasswordCharacters(changedMailPassword)) {
      errorMessage = 'パスワードに使用できない文字が入力されています';
      return;
    }

    if (currentMailPassword === changedMailPassword) {
      errorMessage = '現在のパスワードと異なるパスワードを指定してください';
      return;
    }

    isLoading = true;

    const bundleOptionRepository = RepositoryFactory[BUNDLE_OPTION];
    bundleOptionRepository
      .changeMailIspPassword({
        bundleOptionId: props.bundleOptionId,
        serviceCode: props.serviceCode,
        emailPassword: changedMailPassword
      })
      .then((res) => {
        isLoading = false;
        pageStatus = 'COMPLETE';
      })
      .catch((error) => errorProcess(error));
  };

  const errorProcess = (error: any) => {
    if (error.response.status === 403) {
      alert('セッションが切れました。再度ログインしてください');
      loginUser.logout();
      location.reload();
    } else {
      alert('エラーが発生しました。再度やり直してください。');
    }
    isLoading = false;
  };

  const handleTransitionBack = (): void => {
    pageStatus = 'BACK_MAIL';
  };
</script>

{#if isLoading}
  <Loading />
{/if}

{#if pageStatus === 'INPUT'}<!-- 入力画面 -->
  <div class="h-auto p-6 bg-white m-auto max-w-4xl">
    <div class="mb-6">
      <div class="container flex justify-between">
        <h2 class="text-xl font-bold text-gray-700">メールパスワード変更</h2>
      </div>
    </div>
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="mb:font-sm w-full">
          <ul class="list-none mt-4">
            <li>
              <p>変更後パスワードを入力の上、変更ボタンを押下ください</p>
            </li>
          </ul>

          <div class="flex-1 my-6">
            <form>
              <div class="flex flex-col justify-items-center items-center mx-auto">
                <div class="shadow rounded bg-gray-50 w-11/12 content-center p-4">
                  <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
                    <div class="grid-1 justify-self-start w-full">
                      <span class="label-title">現在のパスワード</span>
                    </div>
                    <div class="grid-1 justify-self-start w-full">
                      <p class="label-value">{currentMailPassword}</p>
                    </div>
                  </div>

                  <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
                    <div class="grid-1 justify-self-start w-full">
                      <span class="label-title">変更後のパスワード</span>
                    </div>
                    <div class="grid-1 justify-self-start w-full">
                      <input type="text" maxlength="16" bind:value={changedMailPassword} />
                      {#if errorMessage}
                        <p class="text-red-500 text-xs mt-2">
                          {errorMessage}
                        </p>
                      {/if}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button class="button-secondary md:ml-5 mb-5 w-32" on:click={handleTransitionBack}> 戻る </button>
            <button class="button-primary md:ml-5 mb-5 w-32" on:click={handleTransitionComplete}> 変更 </button>
          </div>
        </div>
      </div>
    </div>
  </div>
{:else if pageStatus === 'BACK_MAIL'}<!-- 戻る メール画面 -->
  <MailIspChangeModal mstServiceId={props.mstServiceId} />
{:else if pageStatus === 'COMPLETE'}<!-- 完了画面 -->
  <div class="h-auto p-6 bg-white m-auto max-w-4xl">
    <div class="mb-6">
      <div class="container flex justify-between">
        <h2 class="text-xl font-bold text-gray-700">メールパスワード変更</h2>
      </div>
    </div>

    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="inline-flex justify-items-center items-center min-w-full mb-20">
          <div class="mb:font-sm w-full">
            <ul class="list-none mt-4">
              <li>
                <p>パスワード変更を受け付けました</p>
              </li>
            </ul>
          </div>
        </div>
        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button class="button-secondary" on:click={handleTransitionBack}> 契約中メール一覧へ </button>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<style lang="postcss">
  p:not([class]) {
    @apply tracking-normal mb-1;
  }
  .label-title {
    @apply text-sm font-medium text-gray-700;
  }
  .label-value {
    @apply text-sm font-medium text-gray-900;
  }
  input[type='text'] {
    @apply rounded border py-2 px-3 text-sm placeholder-gray-400 focus:placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-transparent w-full;
  }
  .button-primary {
    @apply bg-green-400 rounded-lg hover:bg-green-500 text-sm font-semibold text-white focus:outline-none px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }

  .button-secondary {
    @apply bg-gray-300 rounded-lg hover:bg-gray-400 text-gray-500 text-sm font-semibold focus:outline-none px-10 py-2;
  }
</style>
